import { useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../context/themeContext";
import "./IdeaRefinementModal.css";
import {
  X,
  Sparkles,
  Target,
  TrendingUp,
  ShieldCheck,
  Layers,
  Compass,
  ArrowRight,
  Zap,
} from "lucide-react";

const genToken = () =>
  typeof crypto.randomUUID === "function"
    ? crypto.randomUUID() + crypto.randomUUID()
    : Date.now().toString(36) + Math.random().toString(36).substr(2);


const pillars = [
  {
    icon: <Target size={20} />,
    color: "#C1440E",
    title: "Weakness Detection",
    desc: "AI scans your validated idea for structural gaps — overlooked market segments, pricing blind spots, and assumption leaks that could sink traction.",
  },
  {
    icon: <Compass size={20} />,
    color: "#E5622D",
    title: "Market Repositioning",
    desc: "Get alternative positioning angles tested against real demand signals. Find the frame where your idea resonates loudest with the right audience.",
  },
  {
    icon: <ShieldCheck size={20} />,
    color: "#F5A623",
    title: "Assumption Stress-Test",
    desc: "Every startup is built on assumptions. We surface yours, rank them by risk, and give you a structured way to validate them before you burn runway.",
  },
  {
    icon: <TrendingUp size={20} />,
    color: "#C1440E",
    title: "Competitive Edge Analysis",
    desc: "Map your differentiation against competitors in your space. Identify the moat you need to build and the features worth fighting for.",
  },
  {
    icon: <Layers size={20} />,
    color: "#E5622D",
    title: "Iteration Roadmap",
    desc: "A prioritized plan of what to refine first — from pitch narrative to product scope — so every iteration compounds instead of chasing noise.",
  },
  {
    icon: <Zap size={20} />,
    color: "#F5A623",
    title: "Pivot Signals",
    desc: "Early warning indicators that tell you when to refine versus when to pivot. Stop guessing — let data-backed signals guide your next move.",
  },
];

const steps = [
  { num: "01", label: "Re-analyze", detail: "We re-run your idea through updated market data and trend signals." },
  { num: "02", label: "Surface gaps", detail: "AI identifies the weakest assumptions and highest-risk hypotheses." },
  { num: "03", label: "Generate alternatives", detail: "Multiple refined versions of your concept are proposed with rationale." },
  { num: "04", label: "Score & compare", detail: "Each refinement is scored so you can see which direction has the highest potential." },
];

export default function IdeaRefinementModal({ show, onClose }) {
  const { activeTheme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const dark = activeTheme === "dark";

  useEffect(() => {
    if (!show) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div
      className={`irm-overlay ${dark ? "dark-mode" : ""}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="irm-sheet">

        {/* ── Hero bar ─────────────────────────────────── */}
        <div className="irm-hero">
          <div className="irm-hero-glow" />
          <div className="irm-hero-content">
            <div className="irm-badge">
              <Sparkles size={12} />
              Upcoming Feature
            </div>
            <h1 className="irm-title">Idea Refinement</h1>
            <p className="irm-subtitle">
              Your validated idea is a starting point — not a final answer.<br />
              Refinement is where good ideas become <em>fundable, scalable, and defensible.</em>
            </p>
          </div>
          <button className="irm-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* ── Scrollable body ───────────────────────────── */}
        <div className="irm-body">

          {/* What it does */}
          <section className="irm-section">
            <div className="irm-section-label">What It Does</div>
            <p className="irm-section-intro">
              Idea Refinement goes beyond the initial validation score. Using layered AI analysis, it stress-tests
              every dimension of your startup concept — market fit, differentiation, timing, and team readiness —
              then generates a targeted refinement plan that tells you exactly what to fix and in what order.
            </p>
            <div className="irm-pillars-grid">
              {pillars.map((p, i) => (
                <div key={i} className="irm-pillar-card">
                  <div className="irm-pillar-icon" style={{ color: p.color, background: `${p.color}18`, border: `1.5px solid ${p.color}30` }}>
                    {p.icon}
                  </div>
                  <div>
                    <div className="irm-pillar-title">{p.title}</div>
                    <div className="irm-pillar-desc">{p.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Why it matters */}
          <section className="irm-section irm-why-section">
            <div className="irm-section-label">Why It Matters</div>
            <div className="irm-why-card">
              <blockquote className="irm-quote">
                "The biggest risk isn't building the wrong product — it's believing your first version of the idea is the right one."
              </blockquote>
              <div className="irm-why-body">
                <p>
                  Most founders treat validation as a checkbox. They get a score, feel reassured, and start building.
                  But <strong>validation only tells you the idea is plausible</strong> — refinement is what makes it
                  compelling. Investors don't fund validated ideas; they fund ideas with a clear, defensible edge.
                </p>
                <p>
                  Every week spent building on a weak assumption is a week of runway burned. Idea Refinement gives
                  you a structured way to pressure-test your thinking <em>before</em> you commit — so you build
                  faster, raise smarter, and pivot less.
                </p>
                <div className="irm-stats-row">
                  <div className="irm-stat">
                    <span className="irm-stat-num">3×</span>
                    <span className="irm-stat-label">higher investor interest with a refined pitch narrative</span>
                  </div>
                  <div className="irm-stat">
                    <span className="irm-stat-num">60%</span>
                    <span className="irm-stat-label">of startup failures trace back to unvalidated assumptions</span>
                  </div>
                  <div className="irm-stat">
                    <span className="irm-stat-num">2×</span>
                    <span className="irm-stat-label">faster time-to-PMF when repositioning happens early</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* How it works */}
          <section className="irm-section">
            <div className="irm-section-label">How It Works</div>
            <div className="irm-steps-row">
              {steps.map((s, i) => (
                <div key={i} className="irm-step">
                  <div className="irm-step-num">{s.num}</div>
                  <div className="irm-step-label">{s.label}</div>
                  <div className="irm-step-detail">{s.detail}</div>
                  {i < steps.length - 1 && <ArrowRight size={16} className="irm-step-arrow" />}
                </div>
              ))}
            </div>
          </section>

          {/* CTA footer */}
          <div className="irm-footer">
            <div className="irm-footer-left">
              <p className="irm-footer-note">
                Idea Refinement launches as part of the <strong>Pro plan</strong>. Run your first validation now to get priority access when it goes live.
              </p>
            </div>
            <div className="irm-footer-actions">
              <button className="irm-btn-secondary" onClick={onClose}>
                Maybe later
              </button>
              <button
                className="irm-btn-primary"
                onClick={() => { onClose(); navigate(`/idea-validation/${genToken()}/${genToken()}`); }}
              >
                <Sparkles size={14} />
                Start Validating Now
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
